import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@knack/asterisk-react';

import { useSession } from '@/hooks/useSession';
import { usePostSignUpContext } from '@/contexts/PostSignUpContext';
import { getAnalyticsUserTraits, shouldSendAnalytics, trackExperiment } from '@/utils/analytics';
import { getErrorMessage } from '@/utils/errors';
import { ensureFlagsmithIdentity, getFlagValue, isFlagEnabled } from '@/utils/flagsmith';
import { rudderStackAnalytics } from '@/utils/rudderstack';
import { useAppMutation } from './api/mutations/useAppMutation';

export function useCreateFirstApp() {
  const { isCreatingFirstApp, setIsCreatingFirstApp, isSetupWizardEnabled } =
    usePostSignUpContext();

  const session = useSession();
  const { createAppFromSample } = useAppMutation();
  const { presentToast } = useToast();
  const [t] = useTranslation();
  const isLoading = useRef(false);
  const trackEventSent = useRef(false);

  const presentErrorToast = (error: Error) => {
    setIsCreatingFirstApp(false);
    isLoading.current = false;

    const message = getErrorMessage(error, t('components.create_app.create_app_error_message'));
    presentToast({
      title: message
    });
  };

  const trackVariation = async (
    variationId: 'normal' | 'quick' | 'quick-template',
    variationName: string
  ) => {
    if (shouldSendAnalytics(session.user.email) && !trackEventSent.current) {
      await trackExperiment(
        {
          experimentId: 'ab_testing_sign_up_flow',
          experimentName: 'AB Testing Sign Up Flow',
          variationId,
          variationName
        },
        session.account.id
      );

      trackEventSent.current = true;
    }
  };

  useEffect(() => {
    if (isCreatingFirstApp && session.user.id && !isLoading.current) {
      isLoading.current = true;

      void (async () => {
        if (session.account.id) {
          await ensureFlagsmithIdentity(session);
        }

        if (session.user.id && shouldSendAnalytics(session.user.email)) {
          const name = `${session.user.firstName} ${session.user.lastName}`;

          await rudderStackAnalytics.identify(session.user.id, {
            name,
            ...getAnalyticsUserTraits(session)
          });
        }

        const signUpMode = getFlagValue('ab_testing_sign_up_flow');

        switch (signUpMode) {
          case 'quick': {
            if (!isSetupWizardEnabled) {
              const isChecklistEnabled =
                !!session.user.marketing?.checklist && !isFlagEnabled('full_nextgen_access');

              const sampleApp = {
                appId: '66fac4963bea5b3e05faf2e3',
                templateSlug: 'my-app-name',
                appDescription: 'My App Description',
                appCreatedAutomatically: true
              };
              createAppFromSample.mutate(sampleApp, {
                onSuccess: async (response) => {
                  await trackVariation('quick', 'Quick');

                  const builderPath = `${session.account.slug}/${response.app.slug}`;

                  window.location.href = `${isFlagEnabled('full_nextgen_access') ? import.meta.env.PUBLIC_BUILDER_NEXT_URL : import.meta.env.PUBLIC_BUILDER_URL}/${builderPath}${isChecklistEnabled ? '?onboarding=true' : ''}`;
                },
                onError: presentErrorToast
              });
            }
            break;
          }
          case 'quick-template': {
            const sampleApp = {
              appId: '653a40e8e4f3c0173a9f2d93',
              templateSlug: 'simple-contact-manager',
              appDescription: 'Simple app to manage contacts',
              appCreatedAutomatically: true
            };
            createAppFromSample.mutate(sampleApp, {
              onSuccess: async (response) => {
                await trackVariation('quick-template', 'Quick Template');

                const builderPath = `${session.account.slug}/${response.app.slug}`;

                window.location.href = `${isFlagEnabled('full_nextgen_access') ? import.meta.env.PUBLIC_BUILDER_NEXT_URL : import.meta.env.PUBLIC_BUILDER_URL}/${builderPath}`;
              },
              onError: presentErrorToast
            });
            break;
          }
          case 'normal':
          default:
            await trackVariation('normal', 'Normal');

            setIsCreatingFirstApp(false);
            isLoading.current = false;
            break;
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingFirstApp, session]);
}
